<style lang="scss">
.booking-slots-page {
  span[cy="calendar-view"] {
    @include mobile() {
      display: none !important;
    }
  }
}
</style>
<script>
import { mapState } from 'vuex';
import InCellBookingSlotsService from '@/services/InCellBookingSlotsService';
import BookingSlotsCalendar from '@/views/booking-slots/BookingSlotsCalendar.vue';

const LOCAL_STORAGE_FILTERS = {
  DAY: 'inCellBookingSlotsDay',
  STATUS: 'inCellBookingSlotsStatus',
  CALENDAR_VIEW: 'inCellBookingSlotsCalendarView',
};

export default {
  name: 'InCellVisitBookingSlots',
  components: {
    BookingSlotsCalendar,
  },
  filters: {
    formatDate(date) {
      if (!date) return '';
      return new Date(date).toLocaleDateString('en-GB');
    },
  },
  data() {
    let day = 'All';
    if (localStorage.getItem(LOCAL_STORAGE_FILTERS.DAY)) {
      day = localStorage.getItem(LOCAL_STORAGE_FILTERS.DAY);
    }
    return {
      loading: true,
      calendarView: false,
      bookingSlots: [],
      newBookingSlot: {
        name: '',
        slot_begin: '09:00',
        slot_end: '10:00',
        day_of_week: 'monday',
      },
      editModalShow: false,
      currentBookingSlot: null,
      fields: [
        { key: 'day_of_week', label: 'Day' },
        { key: 'time', label: 'Slot Time' },
        { key: 'start_date', label: 'Start Date' },
        { key: 'end_date', label: 'End Date' },
        { key: 'status', label: 'Status' },
        { key: 'actions', label: 'Actions' },
      ],
      daysOfWeek: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      weekdays: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'],
      columns: [
        {
          key: 'name',
          label: 'Slot Name',
        },
        {
          key: 'day_of_week',
          label: 'Day',
        },
        {
          key: 'time',
          label: 'Slot Time',
        },
        {
          key: 'start_date',
          label: 'Start Date',
        },
        {
          key: 'end_date',
          label: 'End Date',
        },
        {
          key: 'status',
          label: 'Status',
        },
        {
          key: 'actions',
          label: 'Actions',
        },
      ],
      currentPage: 1,
      perPage: 10,
      filters: {
        day,
        status: 'all',
      },
    };
  },
  computed: {
    ...mapState({
      prison: state => state.prison.prison,
    }),
    filteredBookingSlots() {
      let filteredBookingSlots = this.bookingSlots || [];
      if (this.filters.day && this.filters.day !== 'All') {
        filteredBookingSlots = filteredBookingSlots.filter(bs => bs.day_of_week.toLowerCase() === this.filters.day.toLowerCase());
      }
      if (this.filters.status && this.filters.status !== 'all') {
        filteredBookingSlots = filteredBookingSlots.filter(bs => {
          if (this.filters.status === 'active') {
            return bs.active === true;
          }
          return bs.active === false;
        });
      }
      return filteredBookingSlots;
    },
    totalRows() {
      return this.filteredBookingSlots.length;
    },
  },
  async mounted() {
    try {
      if (!this.prison) {
        await this.$store.dispatch('prison/getPrison');
      }
      this.setCalendarViewFromLocal();
      await this.getBookingSlots();
      this.loading = false;
    } catch (e) {
      console.error('Error in mounted hook: ', e);
      this.$toast.error('Failed to load booking slot management page.');
    }
  },
  async activated() {
    // Refresh data to ensure calendar displays current booking slots
    await this.getBookingSlots();
  },
  methods: {
    setCalendarViewFromLocal() {
      const view = localStorage.getItem(LOCAL_STORAGE_FILTERS.CALENDAR_VIEW);
      if (view && view === 'true') {
        this.showCalendarView(true);
      }
    },
    showCalendarView(value) {
      this.calendarView = value;
      localStorage.setItem(LOCAL_STORAGE_FILTERS.CALENDAR_VIEW, value);
      if (value) {
        this.filters.day = 'All';
      }
    },
    async getBookingSlots() {
      this.loading = true;
      try {
        const response = await InCellBookingSlotsService.getAllInCellBookingSlots();
        const slots = response.data;

        let mondaySlots = [];
        let tuesdaySlots = [];
        let wednesdaySlots = [];
        let thursdaySlots = [];
        let fridaySlots = [];
        let saturdaySlots = [];
        let sundaySlots = [];

        slots.forEach(bs => {
          switch (bs.day_of_week.toLowerCase()) {
            case 'monday':
              mondaySlots.push(bs);
              break;
            case 'tuesday':
              tuesdaySlots.push(bs);
              break;
            case 'wednesday':
              wednesdaySlots.push(bs);
              break;
            case 'thursday':
              thursdaySlots.push(bs);
              break;
            case 'friday':
              fridaySlots.push(bs);
              break;
            case 'saturday':
              saturdaySlots.push(bs);
              break;
            case 'sunday':
              sundaySlots.push(bs);
              break;
            default:
              console.error('Cant read day of week for ', bs);
              break;
          }
        });

        mondaySlots = mondaySlots.sort(this.sortStartTime);
        tuesdaySlots = tuesdaySlots.sort(this.sortStartTime);
        wednesdaySlots = wednesdaySlots.sort(this.sortStartTime);
        thursdaySlots = thursdaySlots.sort(this.sortStartTime);
        fridaySlots = fridaySlots.sort(this.sortStartTime);
        saturdaySlots = saturdaySlots.sort(this.sortStartTime);
        sundaySlots = sundaySlots.sort(this.sortStartTime);

        this.bookingSlots = [
          ...mondaySlots,
          ...tuesdaySlots,
          ...wednesdaySlots,
          ...thursdaySlots,
          ...fridaySlots,
          ...saturdaySlots,
          ...sundaySlots,
        ];
      } catch (e) {
        console.error('Error fetching booking slots: ', e);
        this.$toast.error('Error fetching booking slots.');
      } finally {
        this.loading = false;
      }
    },
    sortStartTime(bs1, bs2) {
      const bs1Date = new Date();
      bs1Date.setHours(bs1.slot_begin.split(':')[0], bs1.slot_begin.split(':')[1], 0, 0);
      const bs2Date = new Date();
      bs2Date.setHours(bs2.slot_begin.split(':')[0], bs2.slot_begin.split(':')[1], 0, 0);
      return bs1Date - bs2Date;
    },
    saveFilters() {
      if (this.filters.day && this.filters.day.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.DAY, this.filters.day);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.DAY);
      }
      if (this.filters.status && this.filters.status.trim().length > 0) {
        localStorage.setItem(LOCAL_STORAGE_FILTERS.STATUS, this.filters.status);
      } else {
        localStorage.removeItem(LOCAL_STORAGE_FILTERS.STATUS);
      }
    },
    async updateBookingSlotActiveStatus(slotId, isActive) {
      const action = isActive ? 'deactivate' : 'activate';
      const confirmed = await this.$swal({
        title: `Are you sure you want to ${action} this booking slot?`,
        text: `You are about to ${action} this booking slot. It will ${isActive ? 'no longer be available for new bookings.' : 'be available for new bookings.'}`,
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      });

      if (confirmed) {
        this.loading = true;
        try {
          if (isActive) {
            await InCellBookingSlotsService.deactivateBookingSlot(slotId);
            this.$toast.success('Booking slot deactivated successfully!');
          } else {
            await InCellBookingSlotsService.activateBookingSlot(slotId);
            this.$toast.success('Booking slot activated successfully!');
          }
          await this.getBookingSlots();
        } catch (e) {
          console.error(`Error ${action}ing booking slot: `, e);
          this.$toast.error(`Error ${action}ing booking slot.`);
        } finally {
          this.loading = false;
        }
      }
    },
    async toggleBookingSlotActivation(slot) {
      await this.updateBookingSlotActiveStatus(slot._id, slot.active);
    },
    deactivateSlot(item) {
      this.$swal({
        title: 'Are you sure?',
        text: `Are you sure you want to deactivate slot ${item.name}?`,
        icon: 'warning',
        buttons: true,
        dangerMode: true,
      }).then(value => {
        if (value) {
          this.toggleBookingSlotActivation(item);
        }
      });
    },
    activateSlot(item) {
      this.toggleBookingSlotActivation(item);
    },
    removeAllSlots() {
      this.$swal({
        title: 'Are you sure?',
        text: 'Are you sure you want to delete all in-room booking slots?',
        icon: 'info',
        buttons: true,
      }).then(value => {
        if (value) {
          InCellBookingSlotsService.setAllInCellBookingSlotsInactive(this.prison.prison_id).then(() => {
            this.$toast.success('Successfully deleted all in-room booking slots', {
              toastClassName: ['toast-std', 'success-toast'],
            });
            this.getBookingSlots();
          }).catch(err => {
            console.error('error deleting in-cell booking slots', err);
            this.$toast.error('Error deleting all in-room booking slots', {
              toastClassName: ['toast-std', 'warning-toast'],
            });
          });
        }
      });
    },
    editSlot(slot) {
      this.$router.push({ name: 'update-in-cell-booking-slot', params: { bookingSlotId: slot._id } })
    },
  },
};
</script>

<template>
  <div class="theme-color-wrapper p-2 booking-slots-page">
    <div class="calls row">
      <section class="col-12 mb-2">
        <h2 cy="page-title" class="font-weight-bolder mb-1">In-Room Call Booking Slot Management</h2>
        <router-link :to="{ name: 'dashboard' }" class="body-text">
          &laquo; Back to dashboard
        </router-link>
      </section>
      <div class="col-12">
        <section v-if="!loading">
          <div class="row align-items-center my-2">
            <div v-if="!calendarView" class="col-sm-4">
              <div class="form-group">
                <label>Day of week</label>
                <select v-model="filters.day" class="form-control form-control-sm" cy="slots-filter-day" @change="saveFilters">
                  <option value="All">All</option>
                  <option v-for="(day, index) in weekdays" :key="index" :value="day">{{ day }}</option>
                </select>
              </div>
            </div>
            <div class="col-sm-4 align-items-center" style="margin-top: 8px">
              <span v-if="calendarView" class="d-block pointer" cy="list-view" @click="showCalendarView(false)">
                <i class="fas fa-list" /> List View
              </span>
              <span v-if="!calendarView" class="d-block pointer" cy="calendar-view" @click="showCalendarView(true)">
                <i class="fas fa-list" /> Calendar View
              </span>
            </div>
            <div class="col-sm-4 mt-2 mt-sm-0 ml-auto d-flex align-items-center">
              <router-link :to="{name: 'create-in-cell-booking-slot', params: {prisonId: prison.prison_id}}"
                           class="btn btn-primary mr-1"
              >Add new slot
              </router-link>

              <button class="btn btn-danger" @click="removeAllSlots">Set all slots inactive</button>
            </div>
          </div>

          <b-container v-if="!calendarView" class="px-0">
            <b-table
              class="data-table" cy="slot-table"
              hover
              :items="filteredBookingSlots"
              :fields="columns"
              :current-page="currentPage"
              :per-page="perPage"
              :responsive="true"
              show-empty
            >
              <template #cell(time)="data">
                {{ data.item.slot_begin }} - {{ data.item.slot_end }}
              </template>

              <template #cell(start_date)="data">
                {{ data.item.start_date | formatDate }}
              </template>

              <template #cell(end_date)="data">
                {{ data.item.end_date | formatDate }}
              </template>

              <template #cell(status)="data">
                <b-badge :variant="data.item.active ? 'success' : 'danger'">
                  {{ data.item.active ? 'Active' : 'Inactive' }}
                </b-badge>
              </template>

              <template #cell(actions)="data">
                <b-dropdown class="m-0" variant="link" right toggle-class="text-decoration-none" no-caret>
                  <template #button-content>
                    <i class="fas fa-ellipsis-v" />
                  </template>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="editSlot(data.item)"
                  >
                    Edit
                  </b-dropdown-item-button>
                  <b-dropdown-item-button class="full-nested-btn"
                                          @click="toggleBookingSlotActivation(data.item)"
                  >
                    {{ data.item.active ? 'Deactivate' : 'Activate' }}
                  </b-dropdown-item-button>
                </b-dropdown>
              </template>
            </b-table>
            <b-row>
              <b-col md="6" class="my-1">
                <b-pagination v-model="currentPage" :total-rows="totalRows" :per-page="perPage" class="my-0" />
              </b-col>
            </b-row>
          </b-container>

          <div v-if="calendarView">
            <booking-slots-calendar
              :slots="filteredBookingSlots"
              @slot-deactivate="deactivateSlot($event)"
              @slot-activate="activateSlot($event)"
              @slot-edit="editSlot($event)"
            />
          </div>

        </section>
        <div v-else-if="loading" class="mt-1">
          <b-spinner class="d-block" variant="primary" />
        </div>
      </div>
    </div>
  </div>
</template>
